import { Form, FormInstance, Input, InputRef } from 'antd'
import { t } from 'i18next'
import { useEffect, useMemo, useRef } from 'react'
import { SelectSearchReview } from '../../../../../components'
import {
  SearchReviewSavingType,
  searchReviewSavingTypes,
  SearchReviewType,
  searchReviewTypes,
} from '../../../../../constants/constants'
import { SearchReview } from '../../../../../store/reducers/searchReviewReducer'

interface NewTabProps {
  inputContent: string
  setInputContent: (content: string) => void
}

const NewTab = ({ inputContent, setInputContent }: NewTabProps) => {
  const inputRef = useRef<InputRef>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <Form.Item
      label={t('createSearchReviewModal.steps.definitionStep.newReviewLabel')}
      required
      rules={[{ required: true, message: t('errorMessage.isRequired') || '' }]}
      extra={t('createSearchReviewModal.steps.definitionStep.newReviewExtra')}
      name="title"
    >
      <Input
        placeholder={
          t(
            'createSearchReviewModal.steps.definitionStep.newReviewLabelPlaceholder'
          ) || ''
        }
        ref={inputRef}
        value={inputContent}
        onChange={(event) => setInputContent(event.target.value)}
      />
    </Form.Item>
  )
}

interface ExistingTabProps {
  setLiteratureReview: (literatureReview: SearchReview | undefined) => void
  form: FormInstance
}

const ExistingTab = ({ setLiteratureReview, form }: ExistingTabProps) => {
  const onLiteratureReviewSelected = (
    LiteratureReview: SearchReview | undefined
  ) => {
    if (!LiteratureReview) {
      return
    }
    form.setFieldValue('title', LiteratureReview?.title)
    setLiteratureReview(LiteratureReview)
  }

  const selectedReviewType = useMemo(() => {
    return (
      (form.getFieldValue('reviewType') as SearchReviewType) ||
      searchReviewTypes.LITERATURE_REVIEW
    )
  }, [form])

  return (
    <Form layout="vertical">
      <Form.Item
        label={t(
          'createSearchReviewModal.steps.definitionStep.existingReviewLabel'
        )}
        extra={t(
          'createSearchReviewModal.steps.definitionStep.existingReviewExtra'
        )}
      >
        <SelectSearchReview
          onChange={onLiteratureReviewSelected}
          selectedType={selectedReviewType}
        />
      </Form.Item>
    </Form>
  )
}

interface CreateSearchReviewDefinitionStepProps {
  form: FormInstance
  handleLiteratureReview: (literatureReview: SearchReview | undefined) => void
  setReviewTitle: (title: string) => void
  reviewTitle: string
}

const CreateSearchReviewDefinitionStep = ({
  form,
  handleLiteratureReview,
  setReviewTitle,
  reviewTitle,
}: CreateSearchReviewDefinitionStepProps) => {
  const savingType = useMemo(() => {
    if (!form) {
      return searchReviewSavingTypes.NEW
    }

    return (
      searchReviewSavingTypes[
        form.getFieldValue('savingType') as SearchReviewSavingType
      ] || searchReviewSavingTypes.NEW
    )
  }, [form])

  const tabs = {
    NEW: <NewTab inputContent={reviewTitle} setInputContent={setReviewTitle} />,
    EXISTING: (
      <ExistingTab setLiteratureReview={handleLiteratureReview} form={form} />
    ),
  }

  return <>{tabs[savingType]}</>
}

export default CreateSearchReviewDefinitionStep
